import { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Input, FormControl, FormLabel, Select, Textarea, VStack, HStack, Stack, Box, Text,
    Table, Thead, Tbody, Tr, Th, Td, Badge, Checkbox, NumberInput, NumberInputField,
    Heading, Spinner, useToast,
} from '@chakra-ui/react';
import { atualizarReclamacaoManual, validarVendaParaReclamacaoManual } from '../../services/api';
import ProdutoImagem from '../ProdutoImagem';

const STATUS_OPTIONS = [
    ['opened', 'Aberta'],
    ['closed', 'Encerrada'],
];

const RESPONSAVEL_OPTIONS = [
    ['seller', 'Sua vez de responder'],
    ['buyer', 'Aguardando o comprador'],
];

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));
const toDateInput = (v) => {
    if (!v) return '';
    try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; }
};

const EditarReclamacaoManualModal = ({ isOpen, onClose, reclamacao, onUpdated }) => {
    const toast = useToast();
    const [form, setForm] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [itensVenda, setItensVenda] = useState([]);
    const [itensSel, setItensSel] = useState({});
    const [carregandoItens, setCarregandoItens] = useState(false);

    useEffect(() => {
        if (!reclamacao) { setForm(null); return; }
        setForm({
            problem: reclamacao.problem || '',
            title: reclamacao.title || '',
            description: reclamacao.description || '',
            resolution: reclamacao.dados_adicionais?.resolution || '',
            status: reclamacao.status || 'opened',
            action_responsible: reclamacao.action_responsible || 'seller',
            due_date: toDateInput(reclamacao.due_date),
        });
    }, [reclamacao]);

    useEffect(() => {
        if (!isOpen || !reclamacao?.id_venda) return;
        let ativo = true;
        (async () => {
            setCarregandoItens(true);
            const resp = await validarVendaParaReclamacaoManual(reclamacao.id_venda, reclamacao.id);
            if (!ativo) return;
            const itens = resp?.elegivel ? (resp.itens || []) : [];
            setItensVenda(itens);
            const reclamados = {};
            (reclamacao.dados_adicionais?.itens_reclamados || []).forEach((x) => { reclamados[x.produto_id] = Number(x.quantidade); });
            const temReclamados = Object.keys(reclamados).length > 0;
            const init = {};
            for (const it of itens) {
                const disp = it.quantidade_disponivel ?? it.quantidade;
                if (reclamados[it.produto_id] != null) {
                    init[it.produto_id] = { selecionado: true, qty: Math.min(reclamados[it.produto_id], disp > 0 ? disp : reclamados[it.produto_id]) };
                } else if (!temReclamados && disp > 0) {
                    init[it.produto_id] = { selecionado: true, qty: disp };
                } else {
                    init[it.produto_id] = { selecionado: false, qty: disp > 0 ? disp : 0 };
                }
            }
            setItensSel(init);
            setCarregandoItens(false);
        })();
        return () => { ativo = false; };
    }, [isOpen, reclamacao]);

    const toggleItem = (pid) => setItensSel(p => ({ ...p, [pid]: { ...p[pid], selecionado: !p[pid]?.selecionado } }));
    const setQtyItem = (pid, v, max) => {
        const n = Math.max(0, Math.min(Number(v) || 0, max));
        setItensSel(p => ({ ...p, [pid]: { ...p[pid], qty: n } }));
    };

    const salvar = async () => {
        if (!form?.problem?.trim()) {
            toast({ title: 'Descreva o motivo da reclamação', status: 'warning', duration: 3000 });
            return;
        }
        const itens = Object.entries(itensSel)
            .filter(([, i]) => i?.selecionado && (i?.qty ?? 0) > 0)
            .map(([pid, i]) => ({ produto_id: Number(pid), quantidade: Number(i.qty) }));
        if (itensVenda.length > 0 && itens.length === 0) {
            toast({ title: 'Selecione ao menos 1 item para reclamar', status: 'warning', duration: 3000 });
            return;
        }
        const payload = {
            problem: form.problem.trim(),
            title: form.title,
            description: form.description,
            resolution: form.resolution,
            status: form.status,
            action_responsible: form.action_responsible,
            due_date: form.due_date || null,
            itens,
        };
        setSalvando(true);
        try {
            const resp = await atualizarReclamacaoManual(reclamacao.id, payload);
            if (resp?.success) {
                toast({ title: 'Reclamação atualizada', status: 'success', duration: 3000 });
                onUpdated && onUpdated();
                onClose && onClose();
            } else {
                toast({ title: 'Falha ao atualizar', description: resp?.error || 'Erro desconhecido', status: 'error', duration: 6000, isClosable: true });
            }
        } finally { setSalvando(false); }
    };

    if (!form) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '3xl' }} scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Editar reclamação manual</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Heading size="sm" mb={2}>Itens reclamados</Heading>
                            {carregandoItens ? (
                                <HStack py={4} justify="center"><Spinner size="sm" color="orange.400" /><Text fontSize="sm" color="gray.500">Carregando itens...</Text></HStack>
                            ) : itensVenda.length === 0 ? (
                                <Text fontSize="sm" color="gray.500">Sem itens para exibir.</Text>
                            ) : (
                                <>
                                    <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                                        <Table size="sm" variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th></Th>
                                                    <Th>Produto</Th>
                                                    <Th isNumeric>Qtd.</Th>
                                                    <Th isNumeric>Já reclam.</Th>
                                                    <Th isNumeric>Reclamar</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {itensVenda.map((it) => {
                                                    const sel = itensSel[it.produto_id] || {};
                                                    const disp = it.quantidade_disponivel ?? it.quantidade;
                                                    return (
                                                        <Tr key={it.produto_id} opacity={disp <= 0 ? 0.5 : 1}>
                                                            <Td>
                                                                <Checkbox isDisabled={disp <= 0} isChecked={!!sel.selecionado} onChange={() => toggleItem(it.produto_id)} colorScheme="orange" />
                                                            </Td>
                                                            <Td minW={{ base: '160px', md: '220px' }}>
                                                                <HStack align="flex-start" spacing={2}>
                                                                    <Box flexShrink={0}><ProdutoImagem produto={it} width="40px" height="40px" /></Box>
                                                                    <Box>
                                                                        <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>{it.nome}</Text>
                                                                        <Text fontSize="10px" color="gray.500">SKU: {it.sku}</Text>
                                                                        {disp <= 0 && <Badge colorScheme="red" fontSize="9px" mt="2px">Sem saldo</Badge>}
                                                                    </Box>
                                                                </HStack>
                                                            </Td>
                                                            <Td isNumeric>{it.quantidade_total ?? it.quantidade}</Td>
                                                            <Td isNumeric>{it.quantidade_reclamada ?? 0}</Td>
                                                            <Td isNumeric>
                                                                <NumberInput size="xs" min={0} max={disp} step={1}
                                                                    value={sel.qty ?? disp}
                                                                    onChange={(v) => setQtyItem(it.produto_id, v, disp)}
                                                                    isDisabled={!sel.selecionado || disp <= 0}
                                                                    maxW="70px" ml="auto">
                                                                    <NumberInputField textAlign="right" />
                                                                </NumberInput>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                })}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                    <Text fontSize="10px" color="gray.500" mt={1}>
                                        "Já reclam." considera outras reclamações manuais desta venda (não conta esta).
                                    </Text>
                                </>
                            )}
                        </Box>

                        <FormControl isRequired>
                            <FormLabel>Motivo</FormLabel>
                            <Textarea value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} rows={2} />
                        </FormControl>

                        <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>Status</FormLabel>
                                <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                    {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Responsável / etapa</FormLabel>
                                <Select value={form.action_responsible} onChange={(e) => setForm({ ...form, action_responsible: e.target.value })}>
                                    {RESPONSAVEL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </Select>
                            </FormControl>
                        </Stack>

                        <FormControl>
                            <FormLabel>Prazo para responder</FormLabel>
                            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Título</FormLabel>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Descrição</FormLabel>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Resolução</FormLabel>
                            <Textarea value={form.resolution} onChange={(e) => setForm({ ...form, resolution: e.target.value })} rows={2} placeholder="Preencha se a reclamação já foi resolvida" />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" mr={2} onClick={onClose}>Cancelar</Button>
                    <Button colorScheme="orange" onClick={salvar} isLoading={salvando}>Salvar alterações</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditarReclamacaoManualModal;
